'use client'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTranslations } from 'next-intl'
import { Building2, LoaderCircle } from 'lucide-react'
import { useWorkspaces } from '@/hooks/subscriptions/use-workspaces'
import { cn } from '@/lib/utils'
import { GradientTooltip } from '@/components/common/gradient-tooltip'

interface IWorkspaceDropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  selectedWorkspaceId: string | null
  onWorkspaceChange: (workspaceId: string) => void
  onOpenChange?: (open: boolean) => void
  showName?: boolean // Whether to show workspace name (desktop)
}

/**
 * Compact workspace selector for HeroPrompt action bar
 * Allows users to select which workspace to create new projects in
 */
export function WorkspaceDropdown({
  selectedWorkspaceId,
  onWorkspaceChange,
  onOpenChange,
  className,
  showName = false,
  ...props
}: IWorkspaceDropdownProps) {
  const t = useTranslations('homepage_v2.hero')
  const heroT = useTranslations('home_v3.hero')
  const { workspaces, loading } = useWorkspaces()

  const selectedWorkspace = workspaces?.find(
    (w) => w._id.toString() === selectedWorkspaceId,
  )

  // Get all unique workspaces from subscriptions

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'platinum':
        return 'from-amber-200 via-yellow-300 to-amber-200 text-gray-900'
      case 'premium':
        return 'from-purple-500 to-blue-500 text-white'
      case 'basic':
        return 'bg-blue-500 text-white'
      default:
        return 'bg-gray-500 text-white'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center w-6 h-6">
        <LoaderCircle className="size-3.5 md:size-4 animate-spin text-white/60" />
      </div>
    )
  }

  // If user has no workspaces or only 1 workspace, don't show selector
  if (!workspaces || workspaces.length <= 1) {
    return null
  }

  return (
    <GradientTooltip content={heroT('tooltip_workspace')}>
      <div>
        <Select
          data-id="workspace-dropdown-select"
          value={selectedWorkspaceId || undefined}
          onValueChange={onWorkspaceChange}
          onOpenChange={onOpenChange}
        >
          <SelectTrigger
            className={cn(
              'h-6 border-0 bg-transparent hover:bg-white/10 rounded-md flex items-center justify-center gap-1.5 flex-shrink-0',
              'focus:ring-0 focus:ring-offset-0',
              showName
                ? 'p-1.5 w-auto min-w-0 max-w-[200px]'
                : 'p-0 w-6 min-w-6 max-w-6',
              '[&>svg:last-child]:hidden',
              className,
            )}
          >
            <Building2 className="size-3.5 md:size-4 flex-shrink-0" />
            {showName && selectedWorkspace ? (
              <>
                <span className="text-xs text-white/70 truncate hidden md:inline flex-1 min-w-0 max-w-[100px]">
                  {selectedWorkspace.name ||
                    `Workspace ${selectedWorkspace._id.toString().slice(-8)}`}
                </span>
                <span
                  className={cn(
                    'text-[10px] px-1 py-0.5 rounded font-semibold flex-shrink-0 hidden md:inline-block',
                    selectedWorkspace.limits.plan !== 'free'
                      ? `bg-gradient-to-r ${getPlanBadgeColor(selectedWorkspace.limits.plan)}`
                      : `${getPlanBadgeColor(selectedWorkspace.limits.plan)}`,
                  )}
                >
                  {selectedWorkspace.limits.plan.charAt(0).toUpperCase() +
                    selectedWorkspace.limits.plan.slice(1)}
                </span>
              </>
            ) : (
              <SelectValue className="sr-only" />
            )}
          </SelectTrigger>
          <SelectContent className="max-w-sm md:max-w-xl">
            {/* disabled for now */}
            {/* <SelectItem key="new" value="new">
          <div className="flex items-center gap-2">
            <PlusIcon className="size-3 md:size-4 flex-shrink-0" />
            <span className="flex-1 font-medium text-left truncate">
              {t('workspace_selector.new_workspace')}
            </span>
          </div>
        </SelectItem> */}
            {workspaces.map((workspace) => {
              return (
                <SelectItem
                  key={workspace._id.toString()}
                  value={workspace._id.toString()}
                >
                  <div className="w-full text-left">
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex-1 font-medium text-left truncate">
                        {workspace.name ||
                          `Workspace ${workspace._id.toString().slice(-8)}`}
                      </span>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded font-semibold flex-shrink-0 ${workspace.limits.plan !== 'free' ? `bg-gradient-to-r ${getPlanBadgeColor(workspace.limits.plan)}` : `${getPlanBadgeColor(workspace.limits.plan)}`}`}
                      >
                        {workspace.limits.plan.charAt(0).toUpperCase() +
                          workspace.limits.plan.slice(1)}
                      </span>
                    </div>
                  </div>
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>
    </GradientTooltip>
  )
}
